import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { groupSpacesByCategory } from "@/lib/spaceThemes";
import { isPrivateVisibility, type SpaceSummary } from "@mind-sands/shared";
import { useNavigate } from "react-router";
import { UserMenu } from "../../auth/components/UserMenu";
import { SpaceCard } from "./SpaceCard";

function SpaceGrid({
  items,
  onEnter,
}: {
  items: SpaceSummary[];
  onEnter: (slug: string) => void;
}) {
  const featured = items.length === 1;
  return (
    <div
      className={cn(
        "grid gap-4",
        featured ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2",
      )}
    >
      {items.map((space) => (
        <SpaceCard
          key={space.id}
          space={space}
          onEnter={onEnter}
          featured={featured}
        />
      ))}
    </div>
  );
}

export interface LandingPageViewProps {
  spaces: SpaceSummary[];
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
}

/** Skeleton placeholder card shown while spaces load. */
function SpaceCardSkeleton() {
  return (
    <div className="w-full rounded-2xl overflow-hidden border border-sands-200 bg-sands-100">
      <div className="loading-shimmer h-36 w-full" />
      <div className="p-4 space-y-2">
        <div className="loading-shimmer h-4 w-2/3 rounded" />
        <div className="loading-shimmer h-3 w-full rounded" />
      </div>
    </div>
  );
}

/**
 * Landing page — emotion-first space gallery.
 * Shows every available space grouped by emotional category,
 * each with a visual preview so users can choose at a glance.
 */
export function LandingPageView({
  spaces,
  isLoading = false,
  error = null,
  onRetry,
}: LandingPageViewProps) {
  const navigate = useNavigate();

  const handleEnterSpace = (slug: string) => {
    if (!slug) return;
    navigate(`/spaces/${slug}`);
  };

  const publicSpaces = spaces.filter((s) => !isPrivateVisibility(s.visibility));
  const hiddenSpaces = spaces.filter((s) => isPrivateVisibility(s.visibility));
  const spacesByCategory = groupSpacesByCategory(publicSpaces);

  return (
    <div className="min-h-screen bg-sands-50 flex flex-col">
      <nav className="flex items-center justify-end px-6 pt-4">
        <UserMenu />
      </nav>

      <header className="pt-8 pb-6 px-6 text-center animate-fade-in">
        <h1 className="text-4xl md:text-5xl font-light text-sands-800 tracking-tight mb-3">
          Orymia
        </h1>
        <p className="text-lg text-sands-500 max-w-md mx-auto leading-relaxed font-light italic">
          Where your inner world finds space
        </p>
      </header>

      <div className="px-6 text-center animate-fade-in">
        <p className="text-xl text-sands-600 max-w-md mx-auto leading-relaxed">
          Today, where do you want to rest?
        </p>
      </div>

      <main className="flex-1 px-6 py-10 max-w-3xl mx-auto w-full">
        {error && (
          <div className="text-center py-12">
            <p className="text-sm text-red-500 mb-4">{error}</p>
            {onRetry && (
              <Button variant="secondary" size="sm" onClick={onRetry}>
                Try again
              </Button>
            )}
          </div>
        )}

        {isLoading && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <SpaceCardSkeleton key={i} />
            ))}
          </div>
        )}

        {!isLoading &&
          !error &&
          spacesByCategory.length === 0 &&
          hiddenSpaces.length === 0 && (
          <div className="text-center py-16">
            <p className="text-sands-500 leading-relaxed">
              No spaces are open right now. Check back soon.
            </p>
          </div>
        )}

        {!isLoading &&
          !error &&
          (spacesByCategory.length > 0 || hiddenSpaces.length > 0) && (
          <div className="space-y-10">
            {spacesByCategory.map(({ category, theme, items }) => (
              <section key={category}>
                <h2 className="flex items-center gap-2 text-base font-medium text-sands-700 mb-4">
                  <span className="text-xl">{theme.emoji}</span>
                  {theme.label}
                </h2>
                <SpaceGrid items={items} onEnter={handleEnterSpace} />
              </section>
            ))}
            {hiddenSpaces.length > 0 && (
              <section>
                <h2 className="flex items-center gap-2 text-base font-medium text-sands-700 mb-1">
                  Hidden from landing
                </h2>
                <p className="text-sm text-sands-400 mb-4">
                  Only you can see these. Open one to edit it or show it again.
                </p>
                <SpaceGrid items={hiddenSpaces} onEnter={handleEnterSpace} />
              </section>
            )}
          </div>
        )}
      </main>

      <footer className="py-6 text-center text-xs text-sands-400">
        <p>Orymia — Your mental sandbox, your private world.</p>
      </footer>
    </div>
  );
}
